/* istanbul ignore file */
import { AxiosInstance } from 'axios';
import { AuthClient } from '.';
import { Tenant } from '../models/TenantModels';
import { AuditHistoryRequest, AuditHistoryResponse } from '../models/EventHistoryModels';
import {
  ConfigureIdpRequest,
  GetIdpsResponse,
  GetRolesResponse,
  IdpResponse,
  SamlProviderConfig,
  ValidateIdpRequest,
  ValidateIdpResponse,
} from '../models/IdpModels';
import { DomainsResponse, PagingRequest, TaniumInstance, PermissionsResponse } from '../types';
import { Permission } from '../models/Permissions';
import { mockPermissions } from './mockApi';

export class AuthenticatedClient implements AuthClient {
  apiClient: AxiosInstance;

  constructor(apiClient: AxiosInstance) {
    this.apiClient = apiClient;
  }

  async getTenant(): Promise<Tenant> {
    const { data } = await this.apiClient.get<Tenant>('/v1/tenants/self');
    return Promise.resolve(data);
  }

  async getLinkedInstances(): Promise<TaniumInstance[]> {
    const { data } = await this.apiClient.get<TaniumInstance[]>('/v1/instances');
    return Promise.resolve(data);
  }

  async getIdps(pagingRequest: PagingRequest): Promise<GetIdpsResponse> {
    const { data } = await this.apiClient.get<GetIdpsResponse>('/v1/idps', { params: { ...pagingRequest } });
    return Promise.resolve(data);
  }

  async getRoles(): Promise<GetRolesResponse> {
    const { data } = await this.apiClient.get<GetRolesResponse>('/v1/idp-roles');
    return data;
  }

  async createIdp(idp: ConfigureIdpRequest): Promise<IdpResponse> {
    const { data } = await this.apiClient.post<IdpResponse>('/v1/idps', idp);
    return Promise.resolve(data);
  }

  async updateIdp(idp: ConfigureIdpRequest): Promise<void> {
    await this.apiClient.put<IdpResponse>(`/v1/idps/${idp.id!}`, idp);
  }

  async getDomains(pagingRequest: PagingRequest): Promise<DomainsResponse> {
    const { data } = await this.apiClient.get<DomainsResponse>('/v1/domains', { params: { ...pagingRequest } });
    return Promise.resolve(data);
  }

  async deleteDomain(domainId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/domains/${domainId}`);
    return Promise.resolve();
  }

  async checkDomain(domainId: string): Promise<void> {
    await this.apiClient.post<void>(`/v1/domains/${domainId}/check`);
    return Promise.resolve();
  }

  async deleteIdp(idpId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/idps/${idpId}`);
  }

  async validateIdp(validateReq: ValidateIdpRequest): Promise<ValidateIdpResponse> {
    const { data } = await this.apiClient.post<ValidateIdpResponse>(
      '/v1/idp-setup/identity-provider-validation',
      validateReq
    );
    return Promise.resolve(data);
  }

  async getIdpSigningCert(): Promise<string> {
    const { data } = await this.apiClient.get<string>('/v1/idp-setup/service-signing-cert.crt');
    return Promise.resolve(data);
  }

  async getServiceProviderMetadataXml(idpName: string): Promise<string> {
    const { data } = await this.apiClient.get<string>(`/v1/idp-setup/service-provider-metadata.xml?idpName=${idpName}`);
    return Promise.resolve(data);
  }

  async getSamlConfig(idpName: string): Promise<SamlProviderConfig> {
    const { data } = await this.apiClient.get<SamlProviderConfig>('/v1/idp-setup/service-provider-metadata', {
      params: { idpName },
    });
    return Promise.resolve(data);
  }

  async getEventHistory(auditHistoryRequest: AuditHistoryRequest): Promise<AuditHistoryResponse> {
    const { data } = await this.apiClient.get<AuditHistoryResponse>('/v1/event-history', {
      params: {
        ...auditHistoryRequest,
      },
    });
    return Promise.resolve(data);
  }

  async deleteLinkedInstance(instanceId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/instances/${instanceId}`);
    return Promise.resolve();
  }

  async addDomain(domainName: string): Promise<void> {
    await this.apiClient.post<void>('/v1/domains', { name: domainName });
    return Promise.resolve();
  }

  async downloadFile(href: string): Promise<Blob> {
    const { data } = await this.apiClient.get<BlobPart>(href, { responseType: 'blob' });
    return new Blob([data]);
  }

  async getPermissions(): Promise<PermissionsResponse> {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_API_DATA === 'true') {
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((r) => setTimeout(r, 1000));
      return Promise.resolve({ permissions: mockPermissions });
    }
    const { data } = await this.apiClient.get<{ permissions: Permission[] }>('/v1/permissions');
    return Promise.resolve(data);
  }
}
